import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  MoreHorizontal,
  Edit3,
  Copy,
  Star,
  Users,
  Trash2,
  Globe,
  User,
  Eye,
} from 'lucide-react';
import { Dashboard } from '../../types/dashboard';
import { useAuth } from '../../hooks/useAuth';

interface DashboardTabBarProps {
  dashboards: Dashboard[];
  activeDashboardId?: string;
  onTabClick: (dashboardId: string) => void;
  onAddDashboard: () => void;
  onRenameDashboard?: (dashboardId: string, newName: string) => void;
  onDuplicateDashboard?: (dashboardId: string) => void;
  onMakeDefault?: (dashboardId: string) => void;
  onDeleteDashboard?: (dashboardId: string) => void;
  onChangeVisibility?: (dashboardId: string) => void;
  isLoading?: boolean;
}

interface DashboardContextMenuProps {
  dashboard: Dashboard;
  isOpen: boolean;
  position: { x: number; y: number };
  onClose: () => void;
  onRename: () => void;
  onDuplicate: () => void;
  onMakeDefault: () => void;
  onChangeVisibility: () => void;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
  isPersonal: boolean;
}

const DashboardContextMenu: React.FC<DashboardContextMenuProps> = ({
  dashboard,
  isOpen,
  position,
  onClose,
  onRename,
  onDuplicate,
  onMakeDefault,
  onChangeVisibility,
  onDelete,
  canEdit,
  canDelete,
  isPersonal,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const getVisibilityIcon = () => {
    switch (dashboard.visibility) {
      case 'personal':
        return <User className="h-3 w-3" />;
      case 'role':
        return <Users className="h-3 w-3" />;
      case 'public':
        return <Globe className="h-3 w-3" />;
      default:
        return <Eye className="h-3 w-3" />;
    }
  };

  const getVisibilityLabel = () => {
    switch (dashboard.visibility) {
      case 'personal':
        return 'Personal';
      case 'role':
        return `Role (${dashboard.roleIds?.length || 0} roles)`;
      case 'public':
        return 'Public';
      default:
        return 'Unknown';
    }
  };

  return (
    <div
      ref={menuRef}
      className="fixed z-[110] bg-white rounded-xl shadow-2xl border border-gray-200 py-2 min-w-[200px]"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translateY(-100%)',
      }}
    >
      {/* Dashboard Info */}
      <div className="px-4 py-2 border-b border-gray-100">
        <p className="text-sm font-medium text-gray-900 truncate">{dashboard.name}</p>
        <div className="flex items-center space-x-1 mt-1">
          {getVisibilityIcon()}
          <span className="text-xs text-gray-500">{getVisibilityLabel()}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="py-1">
        {canEdit && (
          <button
            onClick={() => {
              onRename();
              onClose();
            }}
            className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Edit3 className="h-4 w-4" />
            <span>Rename</span>
          </button>
        )}

        <button
          onClick={() => {
            onDuplicate();
            onClose();
          }}
          className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Copy className="h-4 w-4" />
          <span>Duplicate</span>
        </button>

        {isPersonal && (
          <button
            onClick={() => {
              onMakeDefault();
              onClose();
            }}
            className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Star className="h-4 w-4" />
            <span>Make Default</span>
          </button>
        )}

        {canEdit && (
          <button
            onClick={() => {
              onChangeVisibility();
              onClose();
            }}
            className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <Users className="h-4 w-4" />
            <span>Change Visibility</span>
          </button>
        )}

        {canDelete && (
          <>
            <div className="border-t border-gray-100 my-1" />
            <button
              onClick={() => {
                onDelete();
                onClose();
              }}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export const DashboardTabBar: React.FC<DashboardTabBarProps> = ({
  dashboards,
  activeDashboardId,
  onTabClick,
  onAddDashboard,
  onRenameDashboard,
  onDuplicateDashboard,
  onMakeDefault,
  onDeleteDashboard,
  onChangeVisibility,
  isLoading = false,
}) => {
  const { user } = useAuth();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    dashboard: Dashboard;
    position: { x: number; y: number };
  } | null>(null);

  // Sort dashboards: personal defaults first, then recent, then others
  const sortedDashboards = [...dashboards].sort((a, b) => {
    // Personal dashboards with default flag first
    const aIsPersonalDefault = a.visibility === 'personal' && a.isDefaultForUser;
    const bIsPersonalDefault = b.visibility === 'personal' && b.isDefaultForUser;

    if (aIsPersonalDefault && !bIsPersonalDefault) return -1;
    if (!aIsPersonalDefault && bIsPersonalDefault) return 1;

    // Personal dashboards second
    const aIsPersonal = a.visibility === 'personal';
    const bIsPersonal = b.visibility === 'personal';

    if (aIsPersonal && !bIsPersonal) return -1;
    if (!aIsPersonal && bIsPersonal) return 1;

    // Then by most recent activity
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const updateScrollButtons = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setCanScrollLeft(container.scrollLeft > 0);
    setCanScrollRight(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => updateScrollButtons();
    container.addEventListener('scroll', handleScroll);

    // Initial check
    updateScrollButtons();

    // Check on resize
    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver.disconnect();
    };
  }, [updateScrollButtons, dashboards]);

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({ left: -200, behavior: 'smooth' });
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.scrollBy({ left: 200, behavior: 'smooth' });
  };

  const handleContextMenu = (event: React.MouseEvent, dashboard: Dashboard) => {
    event.preventDefault();
    event.stopPropagation();

    setContextMenu({
      dashboard,
      position: { x: event.clientX, y: event.clientY },
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  const handleTabClick = (dashboardId: string, event: React.MouseEvent) => {
    // Don't trigger tab change if it's a context menu click
    if (event.button === 2) return;
    onTabClick(dashboardId);
  };

  const canEditDashboard = (dashboard: Dashboard) => {
    if (dashboard.visibility === 'personal') {
      return dashboard.ownerUserId === user?.id;
    }
    // For public and role dashboards, check if user is creator or admin
    return dashboard.createdBy._id === user?.id || user?.role === 'admin';
  };

  const canDeleteDashboard = (dashboard: Dashboard) => {
    // Same logic as edit for now
    return canEditDashboard(dashboard);
  };

  const getVisibilityIcon = (visibility: Dashboard['visibility']) => {
    switch (visibility) {
      case 'personal':
        return <User className="h-3 w-3 text-blue-500" />;
      case 'role':
        return <Users className="h-3 w-3 text-purple-500" />;
      case 'public':
        return <Globe className="h-3 w-3 text-green-500" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center space-x-4 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex space-x-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex items-center bg-white border-b border-gray-200">
      {/* Left Scroll Button */}
      {canScrollLeft && (
        <button
          onClick={scrollLeft}
          className="absolute left-2 z-10 p-1 rounded-md bg-white shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <ChevronLeft className="h-4 w-4 text-gray-600" />
        </button>
      )}

      {/* Tab Container */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-x-auto scrollbar-none"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingLeft: canScrollLeft ? '40px' : '24px',
          paddingRight: '140px', // Space for Add button
        }}
      >
        <div className="flex items-center space-x-1 py-3">
          {sortedDashboards.map(dashboard => (
            <div
              key={dashboard._id}
              className={`group relative flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition-all whitespace-nowrap ${
                dashboard._id === activeDashboardId
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={e => handleTabClick(dashboard._id, e)}
              onContextMenu={e => handleContextMenu(e, dashboard)}
            >
              <div className="flex items-center space-x-2">
                {getVisibilityIcon(dashboard.visibility)}
                <span className="truncate max-w-[150px]">{dashboard.name}</span>
                {dashboard.isDefaultForUser && dashboard.visibility === 'personal' && (
                  <Star className="h-3 w-3 text-yellow-500 fill-current" />
                )}
              </div>

              {/* Context Menu Trigger */}
              <button
                onClick={e => {
                  e.stopPropagation();
                  handleContextMenu(e, dashboard);
                }}
                className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-black/10 transition-opacity"
              >
                <MoreHorizontal className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Right Scroll Button */}
      {canScrollRight && (
        <button
          onClick={scrollRight}
          className="absolute right-32 z-10 p-1 rounded-md bg-white shadow-md border border-gray-200 hover:bg-gray-50 transition-colors"
          style={{ top: '50%', transform: 'translateY(-50%)' }}
        >
          <ChevronRight className="h-4 w-4 text-gray-600" />
        </button>
      )}

      {/* Sticky Add Dashboard Button */}
      <div
        className="absolute right-6 z-10 flex items-center"
        style={{ top: '50%', transform: 'translateY(-50%)' }}
      >
        <button
          onClick={onAddDashboard}
          className="flex items-center space-x-2 px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors shadow-sm"
        >
          <Plus className="h-4 w-4" />
          <span>Add Dashboard</span>
        </button>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <DashboardContextMenu
          dashboard={contextMenu.dashboard}
          isOpen={true}
          position={contextMenu.position}
          onClose={closeContextMenu}
          onRename={() =>
            onRenameDashboard?.(contextMenu.dashboard._id, contextMenu.dashboard.name)
          }
          onDuplicate={() => onDuplicateDashboard?.(contextMenu.dashboard._id)}
          onMakeDefault={() => onMakeDefault?.(contextMenu.dashboard._id)}
          onChangeVisibility={() => onChangeVisibility?.(contextMenu.dashboard._id)}
          onDelete={() => onDeleteDashboard?.(contextMenu.dashboard._id)}
          canEdit={canEditDashboard(contextMenu.dashboard)}
          canDelete={canDeleteDashboard(contextMenu.dashboard)}
          isPersonal={contextMenu.dashboard.visibility === 'personal'}
        />
      )}
    </div>
  );
};
